import {
  checkDeliverability,
  deliveryNotice,
  haversineKm,
  isOnlineStatus,
} from "../detail/deliverability";
import { cleanSupplierName, coordinatesOf } from "../detail/detailMappers";
import {
  amountCurrency,
  amountValue,
  materialPrice,
  productPriceRange,
} from "./accessoryMoney";

export const ACCESSORY_CODE = "ACCESSORY";
export const ALL_CATEGORY_KEY = "ALL";

const numberOr = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") return fallback;

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
};

export const pickList = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  return [];
};

export const resolveAccessoryOption = (options) => {
  const list = options || [];

  return (
    list.find((option) => option?.accessory === true) ||
    list.find((option) => option?.code === ACCESSORY_CODE) ||
    null
  );
};

export const mapCategoryChips = (categories) => [
  { key: ALL_CATEGORY_KEY, code: null, label: "All" },
  ...(categories || [])
    .filter((category) => category?.code)
    .map((category) => ({
      key: category.code,
      code: category.code,
      label: category.defaultTitle || category.code,
    })),
];

export const accessoryMaterials = (materials, optionCode = ACCESSORY_CODE) =>
  (materials || [])
    .filter((material) =>
      (material?.associatedOptionCodes || []).includes(optionCode)
    )
    .map((material) => ({
      id: material.id,
      code: material.code || null,
      label: material.defaultTitle || material.code || "",
    }));

export const productImages = (dto) => {
  const seen = new Set();
  const images = [];

  for (const raw of dto?.imageUrls || []) {
    const url = typeof raw === "string" ? raw.trim() : "";

    if (!url || seen.has(url)) continue;

    seen.add(url);
    images.push(url);
  }

  return images;
};

export const categoryCodeOf = (category) =>
  (typeof category === "string" ? category : category?.code) || null;

export const categoryTitleOf = (category) =>
  (typeof category === "string"
    ? category
    : category?.defaultTitle || category?.code) || "";

const mapCardMaterials = (dto) =>
  (dto?.containerMaterials || []).map((material) => {
    const price = materialPrice(dto, material?.id);

    return {
      id: material?.id ?? null,
      code: material?.code || null,
      label: material?.defaultTitle || material?.code || "",
      price: price.value,
      currency: price.currency,
      inStock: dto?.inStock !== false && material?.inStock !== false,
    };
  });

const mapCardSupplier = (dto) => {
  const supplier = dto?.supplier;

  if (!supplier) {
    return {
      supplierId: null,
      supplierName: "",
      supplierLogoUrl: null,
      supplierStatus: null,
      supplierOnline: true,
      supplierDeliveryRadiusMeters: null,
      supplierDeliveryRadiusKm: null,
      supplierLatitude: null,
      supplierLongitude: null,
      supplierCoordinates: null,
    };
  }

  const coordinates = coordinatesOf(supplier.address);
  const radiusMeters = numberOr(supplier.deliveryRadiusMeters);

  return {
    supplierId: supplier.id ?? null,
    supplierName: cleanSupplierName(supplier.name),
    supplierLogoUrl: supplier.referenceUrlLogo || null,
    supplierStatus: supplier.status || null,
    supplierOnline: isOnlineStatus(supplier.status),
    supplierDeliveryRadiusMeters: radiusMeters,
    supplierDeliveryRadiusKm: radiusMeters === null ? null : radiusMeters / 1000,
    supplierLatitude: coordinates?.latitude ?? null,
    supplierLongitude: coordinates?.longitude ?? null,
    supplierCoordinates: coordinates,
  };
};

export const resolveDistanceKm = (dto, supplier, origin) => {
  const serverMeters = numberOr(dto?.distanceToLocationMeters);

  if (serverMeters !== null && serverMeters >= 0) return serverMeters / 1000;

  return haversineKm(origin, supplier.supplierCoordinates);
};

export const mapAccessoryCard = (dto, origin) => {
  if (!dto) return null;

  const supplier = mapCardSupplier(dto);
  const distanceKm = resolveDistanceKm(dto, supplier, origin);

  const price = amountValue(dto.amount);
  const currency = amountCurrency(dto.amount);
  const originalPrice = amountValue(dto.originalAmount);
  const range = productPriceRange(dto);

  const images = productImages(dto);

  const { deliverable, reason, radiusKm, distanceKm: blockedKm } =
    checkDeliverability({
      online: supplier.supplierOnline,
      deliveryRadiusKm: supplier.supplierDeliveryRadiusKm,
      distanceKm,
    });

  const notice = deliverable
    ? null
    : deliveryNotice({
        reason,
        name: supplier.supplierName,
        radiusKm,
        distanceKm: blockedKm,
      });

  return {
    id: dto.id,
    name: dto.name || "",
    description: dto.description || "",

    images,
    imageUrl: images[0] ?? null,

    categoryCode: categoryCodeOf(dto.category),
    categoryTitle: categoryTitleOf(dto.category),

    price,
    currency,
    originalPrice,
    hasDiscount: Number.isFinite(originalPrice) && originalPrice > price,

    priceFrom: range.from,
    priceTo: range.to,
    hasPriceRange: range.isRange,

    inStock: dto.inStock !== false,
    favourited: dto.favourited ?? false,
    favouritable: dto.favourited !== null && dto.favourited !== undefined,

    distanceMeters: numberOr(dto.distanceToLocationMeters),
    distanceKm,

    containerOption: dto.containerOption || null,
    containerMaterials: mapCardMaterials(dto),

    ...supplier,

    deliverable,
    notice,
  };
};

export const mapSearchPage = (data, origin) => ({
  items: (data?.content || [])
    .map((dto) => mapAccessoryCard(dto, origin))
    .filter(Boolean),
  total: numberOr(data?.totalElements, 0),
  totalPages: numberOr(data?.totalPages, 0),
  page: numberOr(data?.number, 0),
  pageSize: numberOr(data?.size, 0),
  isLast: data?.last !== false,
});

export const mapPriceBounds = (data) => {
  const minPrice = numberOr(data?.minPrice);
  const maxPrice = numberOr(data?.maxPrice);

  return {
    minPrice,
    maxPrice,
    currency: data?.currency || null,
    count: numberOr(data?.count, 0),
    hasBounds: minPrice !== null && maxPrice !== null,
    isFlat: minPrice !== null && maxPrice !== null && minPrice === maxPrice,
  };
};

export const isAccessoryProduct = (dto) => dto?.containerOption?.accessory === true;

export const specRows = (dto) => {
  const accessory = isAccessoryProduct(dto);

  const materials = (dto?.containerMaterials || [])
    .map((material) => material?.defaultTitle || material?.code)
    .filter(Boolean)
    .join(", ");

  const rows = [
    { label: "Brand", value: dto?.brand?.defaultTitle || "" },
    { label: "Country Of Origin", value: dto?.countryOfOrigin?.name || "" },
    { label: "Fuel Type", value: dto?.fuelType?.defaultTitle || "" },
    accessory
      ? { label: "Material", value: materials }
      : { label: "Cylinder Type", value: dto?.containerType?.defaultTitle || "" },
  ];

  if (!accessory) {
    const volume = numberOr(dto?.volume);

    rows.push({ label: "Cylinder Volume", value: volume ? `${volume} Kg` : "" });
  }

  return rows.filter((row) => row.value);
};

export const supplierRatingOf = (data) => ({
  average: numberOr(data?.averageRating, 0),
  total: numberOr(data?.totalRatings, 0),
});
